import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { motion } from 'framer-motion';
import { createGeneratorActor } from '../services/generator';
import { useAuth } from '../auth/useAuth';

interface QRClaimProps {
  tokenId: number;
  qrData: string;
}

export default function QRClaim({ tokenId, qrData }: QRClaimProps) {
  const [isClaiming, setIsClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { agent, principal, isAuthenticated, loginWithII } = useAuth();

  useEffect(() => {
    // Auth handled globally
  }, []);

  const handleConnect = async () => {
    try {
      await loginWithII();
    } catch (err: any) {
      setError(err.message || 'Failed to connect wallet');
    }
  };

  const handleClaim = async () => {
    if (!agent || !isAuthenticated) {
      setError('Please connect your wallet first');
      return;
    }

    setIsClaiming(true);
    setError(null);

    try {
      // Create authenticated actor
      const actor = await createGeneratorActor(agent);
      const result = await actor.claim_nft_via_qr(qrData);
      
      setClaimed(true);
    } catch (err: any) {
      setError(err.message || 'Failed to claim NFT');
    } finally {
      setIsClaiming(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-dark rounded-2xl p-8 max-w-md mx-auto text-center"
    >
      <h2 className="text-3xl font-bold mb-6 text-gold">Claim Your IC Spicy NFT</h2>
      
      {!claimed ? (
        <>
          <div className="mb-6 p-4 bg-black/30 rounded-lg">
            <QRCodeSVG value={qrData} size={256} className="mx-auto" />
          </div>
          
          <p className="text-gray-300 mb-4">Token ID: #{tokenId}</p>
          
          {!isAuthenticated ? (
            <button
              onClick={handleConnect}
              className="w-full bg-gold hover:bg-yellow-600 text-white font-bold py-3 px-6 rounded-lg transition-all transform hover:scale-105 mb-4"
            >
              Connect Wallet
            </button>
          ) : (
            <>
              <p className="text-green-400 mb-4">✓ Wallet Connected</p>
              <button
                onClick={handleClaim}
                disabled={isClaiming}
                className="w-full bg-gold hover:bg-yellow-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded-lg transition-all transform hover:scale-105"
              >
                {isClaiming ? 'Claiming...' : 'Claim NFT'}
              </button>
            </>
          )}
        </>
      ) : (
        <div className="text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="text-6xl mb-4"
          >
            ✓
          </motion.div>
          <h3 className="text-2xl font-bold text-gold mb-2">NFT Claimed!</h3>
          <p className="text-gray-300">Your IC Spicy RWA NFT has been added to your wallet.</p>
        </div>
      )}
      
      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-4 p-3 bg-red-900/30 border border-red-500 rounded-lg text-red-200"
        >
          {error}
        </motion.div>
      )}
    </motion.div>
  );
}

